import PropTypes from 'prop-types'

/**
 * CAR-4050: the one footer every pop-up uses - as on "Appointment Already Scheduled" (CAR-4034): an outlined
 * Cancel, focused first so an accidental Enter never confirms, and a navy primary, both in capitals.
 * Pass it as FormModal's `footer`. Leave out `onCancel` or `onConfirm` (or its label) to drop that button;
 * `children` go before them for a screen that needs an extra action. `tone='danger'` makes the primary the
 * destructive red, for a delete. `confirmFormId` makes the primary that form's submit button (the footer sits
 * outside the form), so the form's own validation and Enter-to-save keep working.
 */
export const FORM_MODAL_FOOTER_BUTTON_CLASS =
  'inline-flex items-center justify-center gap-2 rounded-lg px-5 py-2.5 text-[13px] font-semibold uppercase transition-colors'
export const FORM_MODAL_CANCEL_CLASS = `${FORM_MODAL_FOOTER_BUTTON_CLASS} border border-[#D1D5D8] bg-white text-[#0A1628] hover:bg-[#F8FAFC] disabled:cursor-not-allowed disabled:opacity-50`
export const FORM_MODAL_PRIMARY_CLASS = `${FORM_MODAL_FOOTER_BUTTON_CLASS} bg-[#0A1628] text-white hover:bg-[#1F2D3D] disabled:cursor-not-allowed disabled:opacity-50`
export const FORM_MODAL_DANGER_CLASS = `${FORM_MODAL_FOOTER_BUTTON_CLASS} bg-destructive text-white hover:bg-destructive/90 disabled:cursor-not-allowed disabled:opacity-50`

export const FormModalFooter = ({
  onCancel,
  cancelLabel = 'Cancel',
  onConfirm,
  confirmLabel,
  confirmDisabled = false,
  cancelDisabled = false,
  confirmFormId,
  tone = 'primary',
  children
}) => {
  const showCancel = Boolean(onCancel && cancelLabel)
  const showConfirm = Boolean((onConfirm || confirmFormId) && confirmLabel)
  return (
    <div data-testid='form-modal-footer' className='flex flex-wrap justify-end gap-3'>
      {children}
      {showCancel && (
        <button type='button' autoFocus onClick={onCancel} disabled={cancelDisabled} className={FORM_MODAL_CANCEL_CLASS}>
          {cancelLabel}
        </button>
      )}
      {showConfirm && (
        <button
          type={confirmFormId ? 'submit' : 'button'}
          form={confirmFormId}
          autoFocus={!showCancel}
          onClick={onConfirm}
          disabled={confirmDisabled}
          className={tone === 'danger' ? FORM_MODAL_DANGER_CLASS : FORM_MODAL_PRIMARY_CLASS}
        >
          {confirmLabel}
        </button>
      )}
    </div>
  )
}

FormModalFooter.propTypes = {
  onCancel: PropTypes.func,
  cancelLabel: PropTypes.node,
  onConfirm: PropTypes.func,
  confirmLabel: PropTypes.node,
  confirmDisabled: PropTypes.bool,
  cancelDisabled: PropTypes.bool,
  confirmFormId: PropTypes.string,
  tone: PropTypes.oneOf(['primary', 'danger']),
  children: PropTypes.node
}
